/**
 * Living Seeds – "Adicionar" buttons in the cart suggestions (snippets/sv-cart-upsell.liquid).
 *
 * Adds the product with /cart/add.js, asks Shopify for fresh HTML of every cart section
 * on the page, then dispatches the theme's CartAddEvent. cart-items-component morphs the
 * drawer / cart page from those sections (so the free-shipping bar and suggestions update)
 * and cart-icon updates the bubble from itemCount.
 * One delegated listener survives the drawer being re-rendered. If anything fails, the
 * form is submitted normally (Shopify adds the item and opens the cart page).
 */
import { CartAddEvent } from '@theme/events';

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-sv-upsell-form]')) return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  button?.setAttribute('aria-busy', 'true');

  const variantId = Number(new FormData(form).get('id'));

  /** @type {Set<string>} */
  const sectionIds = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.add(element.dataset.sectionId);
    }
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: Array.from(sectionIds).join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

    document.dispatchEvent(
      new CartAddEvent(cart, 'sv-cart-upsell', {
        source: 'sv-cart-upsell',
        itemCount: cart.item_count,
        variantId: String(variantId),
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('Cart suggestion error:', error);
    button?.removeAttribute('aria-busy');
    form.submit();
  }
}

if (!window.svCartUpsellReady) {
  window.svCartUpsellReady = true;
  document.addEventListener('submit', onSubmit);
}
